import {CUSTOM_FIELD_RESOURCES} from '@/components/query/custom-fields';
import Entities from '@/common/api/definitions/entities';
import {specialKeys} from '@/common/spec/resolve-entity';
import SpecResolver from '@/common/spec/spec-resolver';
import customFieldExclusions
    from '@/modules/rules-engine/components/condition-builder/custom-field-exclusions';
import API_SPEC_FIELD_TYPES from '@/components/query/helpers/api-spec-field-types';

export const entityToCustomFieldMap = {
    [Entities.contact]: CUSTOM_FIELD_RESOURCES.contacts,
    [Entities.customer]: CUSTOM_FIELD_RESOURCES.customers,
    [Entities.paymentCard]: CUSTOM_FIELD_RESOURCES.paymentCards,
    [Entities.product]: CUSTOM_FIELD_RESOURCES.products,
    [Entities.subscription]: CUSTOM_FIELD_RESOURCES.subscriptions,
    [Entities.transaction]: CUSTOM_FIELD_RESOURCES.transactions,
    [Entities.website]: CUSTOM_FIELD_RESOURCES.websites,
};

/**
 * Extract an array of custom field resources that are present
 * in the list of pathways for a particular event.
 * @param {Array} pathways
 * @returns {*}
 */
export function extractCustomFieldResources(pathways) {
    return [...new Set(pathways.map(({resource}) => resource))]
        .reduce((requiringFetch, resource) => {
            if (Reflect.has(entityToCustomFieldMap, resource)) {
                return requiringFetch.concat(entityToCustomFieldMap[resource]);
            }
            return requiringFetch;
        }, []);
}

const pathDelimiter = '/';

/**
 * Set special flags for UI handling on custom fields.
 * @param {{}} customField
 * @return {{}}
 */
export function setHandlingFlags(customField) {
    if (customField.type === API_SPEC_FIELD_TYPES.array) {
        customField.$array = true;
    }
    return customField;
}

/**
 * Resolve the entities related to the provided pathways and
 * inject custom field information where required.
 * @param {array} pathways
 * @param {Function} customFieldStore
 * @param {string} delimiter
 * @param {boolean} trim
 */
export default function getFieldsFromPathways(
    pathways,
    customFieldStore = null,
    {delimiter = pathDelimiter, trim = true} = {},
) {
    const groups = {};
    pathways.forEach(({
        path,
        resource,
        exclusions = false,
        category = null,
        fields: specialFields = null,
        type = null,
        autoCompleteFields = null,
    }) => {
        // using special category structure not based on API spec
        if (category) {
            groups[category] = {
                path: category,
                category,
                fields: specialFields,
                lineage: [category.slice(1)],
            };
        } else {
            // using API spec entity
            const resolvedEntity = SpecResolver.resolveEntity(resource);
            if (exclusions) {
                exclusions.forEach(prop => Reflect.deleteProperty(resolvedEntity, prop));
            }
            groups[path] = {
                path,
                resource,
                type,
                autoCompleteFields,
                fields: resolvedEntity,
                lineage: (trim ? path.slice(1) : path).split(delimiter),
            };
            if (customFieldStore && resolvedEntity.$customFields) {
                const stored = customFieldStore(entityToCustomFieldMap[resource]);
                if (stored && stored.fields.length) {
                    const customFields = {};
                    stored.fields
                    // exclude custom fields
                        .filter(({fields}) => !customFieldExclusions.some(p => p(fields)))
                        .forEach(({fields}) => {
                            customFields[fields.name] = setHandlingFlags(fields);
                        });
                    // define node as nested, this is used
                    // by the search provider
                    Reflect.set(customFields, '$nested', true);
                    Reflect.set(resolvedEntity, specialKeys.customFields, customFields);
                    Reflect.deleteProperty(resolvedEntity, '$customFields');
                } else {
                    // delete the custom field flag if no fields exist
                    Reflect.deleteProperty(resolvedEntity, specialKeys.customFields);
                }
            }
        }
    });
    return groups;
}
